import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  getBusinessById,
  getServicesByBusiness,
  getAppointmentsByBusiness,
  getStaffByBusiness,
  createAppointment,
  addService,
  updateService,
  deleteService,
  updateBusinessHours,
  uploadBusinessLogo,
  updateStaffHours,
  addStaff,
  deleteStaff,
  updateAppointmentStatus,
  getBusinessLogoUrl,
} from "../utils/api";
import { validatePhone, PHONE_ERROR_MSG } from "../utils/phoneUtils";
import { generateTimeSlots, minutesToTime, timeToMinutes } from "../utils/timeUtils";
import Modal from "../components/Modal";
import ConfirmModal from "../components/ConfirmModal";
import AlertModal from "../components/AlertModal";
import ServicesTab from "../components/adminPages/ServicesTab";
import AppointmentsTab from "../components/adminPages/AppointmentsTab";
import MyHoursTab from "../components/adminPages/MyHoursTab";
import StaffTab from "../components/adminPages/StaffTab";
import SettingsTab from "../components/adminPages/SettingsTab";
import ReportsTab from "../components/adminPages/ReportsTab";
import BlockedCustomers from "../components/adminPages/BlockedCustomers";
import {
  ListOrdered,
  Settings,
  CalendarDays,
  Users,
  Clock,
  Save,
  X,
  LogOut,
  Shield,
  UserPlus,
  BarChart3,
  Ban,
} from "lucide-react";

const DAY_NAMES = ["Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi", "Pazar"];

const INITIAL_TAB_LOADING = {
  services: false,
  appointments: false,
  myhours: false,
  staff: false,
  settings: false,
};

const INITIAL_TAB_LOADED = {
  services: false,
  appointments: false,
  myhours: false,
  staff: false,
  settings: false,
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();

  const [business, setBusiness] = useState(null);
  const [businessLoading, setBusinessLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(isAdmin ? "services" : "appointments");
  const [tabLoading, setTabLoading] = useState(INITIAL_TAB_LOADING);
  const [tabLoaded, setTabLoaded] = useState(INITIAL_TAB_LOADED);

  // Hizmetler
  const [services, setServices] = useState([]);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceForm, setServiceForm] = useState({
    name: "",
    price: "",
    duration: "",
    staffList: [],
  });
  const [savingService, setSavingService] = useState(false);

  // İşletme çalışma saatleri
  const [workingHours, setWorkingHours] = useState({ start: "", end: "" });
  const [hoursUpdated, setHoursUpdated] = useState(false);
  const [savingHours, setSavingHours] = useState(false);
  const [currentBusinessLogoUrl, setCurrentBusinessLogoUrl] = useState("");
  const [selectedLogoFile, setSelectedLogoFile] = useState(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState("");
  const [savingLogo, setSavingLogo] = useState(false);
  const [logoUpdated, setLogoUpdated] = useState(false);

  // Çalışan kişisel çalışma saatleri
  const [myHours, setMyHours] = useState({ start: "", end: "" });
  const [myClosedDays, setMyClosedDays] = useState([]);
  const [myMaxBookingDays, setMyMaxBookingDays] = useState("10");
  const [myHoursUpdated, setMyHoursUpdated] = useState(false);
  const [savingMyHours, setSavingMyHours] = useState(false);

  // Randevular
  const [appointments, setAppointments] = useState([]);
  const [processingAppointmentId, setProcessingAppointmentId] = useState(null);
  const [showHistoryAppointments, setShowHistoryAppointments] = useState(false);
  const [showManualAppointmentForm, setShowManualAppointmentForm] = useState(false);
  const [manualAppointmentForm, setManualAppointmentForm] = useState({
    staffId: "",
    serviceId: "",
    date: "",
    startTime: "",
    customerName: "",
    customerPhone: "",
  });
  const [manualTimeSlots, setManualTimeSlots] = useState([]);
  const [manualSlotsLoading, setManualSlotsLoading] = useState(false);
  const [savingManualAppointment, setSavingManualAppointment] = useState(false);

  // Çalışanlar
  const [staffList, setStaffList] = useState([]);
  const [showStaffModal, setShowStaffModal] = useState(false);
  const [staffForm, setStaffForm] = useState({
    username: "",
    password: "",
    fullName: "",
    role: "staff",
  });
  const [savingStaff, setSavingStaff] = useState(false);

  // Ortak onay modalı
  const [confirmState, setConfirmState] = useState({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: null,
  });
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [alertState, setAlertState] = useState({
    isOpen: false,
    title: "Bilgi",
    message: "",
    variant: "info",
  });

  const setTabLoadingState = (tab, value) => {
    setTabLoading((prev) => ({ ...prev, [tab]: value }));
  };

  const setTabLoadedState = (tab, value) => {
    setTabLoaded((prev) => ({ ...prev, [tab]: value }));
  };

  const requestConfirmation = ({ title = "Onay", message, onConfirm }) => {
    setConfirmState({
      isOpen: true,
      title,
      message,
      onConfirm,
    });
  };

  const closeConfirmModal = () => {
    if (confirmLoading) return;
    setConfirmState({
      isOpen: false,
      title: "",
      message: "",
      onConfirm: null,
    });
  };

  const handleConfirmModal = async () => {
    if (!confirmState.onConfirm) return;
    try {
      setConfirmLoading(true);
      await confirmState.onConfirm();
      setConfirmState({
        isOpen: false,
        title: "",
        message: "",
        onConfirm: null,
      });
    } finally {
      setConfirmLoading(false);
    }
  };

  const showAlert = ({ title = "Bilgi", message, variant = "info" }) => {
    setAlertState({
      isOpen: true,
      title,
      message,
      variant,
    });
  };

  const closeAlertModal = () => {
    setAlertState({
      isOpen: false,
      title: "Bilgi",
      message: "",
      variant: "info",
    });
  };

  const loadBusiness = useCallback(async (force = false) => {
    if (!user) return;

    try {
      setBusinessLoading(true);
      const biz = await getBusinessById(user.businessId);
      setBusiness(biz);
      setCurrentBusinessLogoUrl(getBusinessLogoUrl(user.businessId, Date.now()));
      if (biz?.workingHours) {
        setWorkingHours({ ...biz.workingHours });
      }
      setTabLoadedState("settings", true);
    } catch (err) {
      console.error("İşletme bilgileri yüklenemedi:", err);
    } finally {
      setBusinessLoading(false);
    }
  }, [user]);

  const loadServices = useCallback(async (force = false) => {
    if (!user) return;
    if (!force && tabLoaded.services) return;

    try {
      setTabLoadingState("services", true);
      const srvList = await getServicesByBusiness(user.businessId);
      setServices(srvList);
      setTabLoadedState("services", true);
    } catch (err) {
      console.error("Hizmetler yüklenemedi:", err);
    } finally {
      setTabLoadingState("services", false);
    }
  }, [user, tabLoaded.services]);

  const loadAppointments = useCallback(async (force = false) => {
    if (!user) return;
    if (!force && tabLoaded.appointments) return;

    try {
      setTabLoadingState("appointments", true);
      const aptList = isAdmin
        ? await getAppointmentsByBusiness(user.businessId)
        : await getAppointmentsByBusiness(user.businessId, null, user.id);

      const sortedApts = [...aptList].sort((a, b) => {
        const dateComp = a.date.localeCompare(b.date);
        if (dateComp !== 0) return dateComp;
        return a.startTime.localeCompare(b.startTime);
      });

      setAppointments(sortedApts);
      setTabLoadedState("appointments", true);
    } catch (err) {
      console.error("Randevular yüklenemedi:", err);
    } finally {
      setTabLoadingState("appointments", false);
    }
  }, [user, isAdmin, tabLoaded.appointments]);

  const syncMyHoursFromStaffList = useCallback((list) => {
    const me = list.find((s) => s.id === user?.id);
    if (!me) return;
    setMyHours({ start: me.workingHours.start, end: me.workingHours.end });
    setMyClosedDays(me.closedDays || []);
    setMyMaxBookingDays(String(me.maxBookingDays ?? 10));
  }, [user]);

  const loadStaff = useCallback(async (force = false) => {
    if (!user) return;
    if (!force && tabLoaded.staff) return;

    try {
      setTabLoadingState("staff", true);
      const stfList = await getStaffByBusiness(user.businessId);
      setStaffList(stfList);
      syncMyHoursFromStaffList(stfList);
      setTabLoaded((prev) => ({ ...prev, staff: true, myhours: true }));
    } catch (err) {
      console.error("Çalışanlar yüklenemedi:", err);
    } finally {
      setTabLoadingState("staff", false);
    }
  }, [user, tabLoaded.staff, syncMyHoursFromStaffList]);

  const loadMyHours = useCallback(async (force = false) => {
    if (!user) return;
    if (!force && tabLoaded.myhours) return;

    try {
      setTabLoadingState("myhours", true);
      const stfList = await getStaffByBusiness(user.businessId);
      setStaffList(stfList);
      syncMyHoursFromStaffList(stfList);
      setTabLoaded((prev) => ({ ...prev, myhours: true, staff: true }));
    } catch (err) {
      console.error("Kişisel çalışma saatleri yüklenemedi:", err);
    } finally {
      setTabLoadingState("myhours", false);
    }
  }, [user, tabLoaded.myhours, syncMyHoursFromStaffList]);

  useEffect(() => {
    setActiveTab(isAdmin ? "services" : "appointments");
  }, [isAdmin]);

  useEffect(() => {
    setTabLoading(INITIAL_TAB_LOADING);
    setTabLoaded(INITIAL_TAB_LOADED);
    setServices([]);
    setAppointments([]);
    setStaffList([]);
    setShowHistoryAppointments(false);
    setSelectedLogoFile(null);
    setLogoPreviewUrl("");
    setLogoUpdated(false);
    if (user) {
      loadBusiness(true);
    }
  }, [user, loadBusiness]);

  useEffect(() => {
    return () => {
      if (logoPreviewUrl) {
        URL.revokeObjectURL(logoPreviewUrl);
      }
    };
  }, [logoPreviewUrl]);

  useEffect(() => {
    if (!user) return;

    if (activeTab === "services" && isAdmin) {
      loadServices();
    }

    if (activeTab === "appointments") {
      loadAppointments();
      loadServices();
      loadStaff();
    }

    if (activeTab === "myhours") {
      loadMyHours();
    }

    if (activeTab === "staff" && isAdmin) {
      loadStaff();
    }
  }, [activeTab, isAdmin, user, loadServices, loadAppointments, loadMyHours, loadStaff]);

  useEffect(() => {
    if (!user) return;
    setManualAppointmentForm((prev) => ({
      ...prev,
      staffId: isAdmin ? prev.staffId : String(user.id),
    }));
  }, [user, isAdmin]);

  const selectedManualStaff = staffList.find(
    (staff) => String(staff.id) === String(isAdmin ? manualAppointmentForm.staffId : user?.id)
  );

  const availableManualServices = services.filter((service) => {
    if (!selectedManualStaff) return false;
    if (!Array.isArray(service.staffList)) return true;
    return service.staffList.map(String).includes(String(selectedManualStaff.id));
  });

  const selectedManualService = availableManualServices.find(
    (service) => String(service.id) === String(manualAppointmentForm.serviceId)
  );

  useEffect(() => {
    if (!user || !selectedManualStaff || !selectedManualService || !manualAppointmentForm.date) {
      setManualTimeSlots([]);
      return;
    }

    const loadManualTimeSlots = async () => {
      try {
        setManualSlotsLoading(true);
        const existingAppointments = await getAppointmentsByBusiness(
          user.businessId,
          manualAppointmentForm.date,
          selectedManualStaff.id
        );

        const generatedSlots = generateTimeSlots(
          selectedManualStaff.workingHours,
          Number(selectedManualService.duration),
          existingAppointments
        );

        const today = toLocalDateString(new Date());
        const nowMinutes = timeToMinutes(
          `${String(new Date().getHours()).padStart(2, "0")}:${String(new Date().getMinutes()).padStart(2, "0")}`
        );

        const filteredSlots = generatedSlots.map((slot) => {
          if (manualAppointmentForm.date !== today) {
            return slot;
          }
          return {
            ...slot,
            available: slot.available && timeToMinutes(slot.time) > nowMinutes,
          };
        });

        setManualTimeSlots(filteredSlots);
      } catch (err) {
        console.error("Manuel randevu saatleri yüklenemedi:", err);
        setManualTimeSlots([]);
      } finally {
        setManualSlotsLoading(false);
      }
    };

    loadManualTimeSlots();
  }, [
    user,
    selectedManualStaff,
    selectedManualService,
    manualAppointmentForm.date,
  ]);

  useEffect(() => {
    if (!manualAppointmentForm.startTime) return;
    const selectedSlot = manualTimeSlots.find((slot) => slot.time === manualAppointmentForm.startTime);
    if (selectedSlot && selectedSlot.available) return;

    setManualAppointmentForm((prev) => ({ ...prev, startTime: "" }));
  }, [manualTimeSlots, manualAppointmentForm.startTime]);

  const handleManualFormChange = (field, value) => {
    setManualAppointmentForm((prev) => {
      const next = { ...prev, [field]: value };

      if (field === "staffId") {
        next.serviceId = "";
        next.startTime = "";
      }

      if (field === "serviceId" || field === "date") {
        next.startTime = "";
      }

      return next;
    });
  };

  const handleCreateManualAppointment = async (e) => {
    e.preventDefault();

    if (!selectedManualStaff || !selectedManualService) {
      showAlert({
        title: "Eksik Bilgi",
        message: "Lütfen çalışan ve hizmet seçin.",
        variant: "warning",
      });
      return;
    }

    if (
      !manualAppointmentForm.date ||
      !manualAppointmentForm.startTime ||
      !manualAppointmentForm.customerName.trim() ||
      !manualAppointmentForm.customerPhone.trim()
    ) {
      showAlert({
        title: "Eksik Bilgi",
        message: "Lütfen tüm zorunlu alanları doldurun.",
        variant: "warning",
      });
      return;
    }

    if (!validatePhone(manualAppointmentForm.customerPhone)) {
      showAlert({
        title: "Geçersiz Numara",
        message: PHONE_ERROR_MSG,
        variant: "warning",
      });
      return;
    }

    const startMinutes = timeToMinutes(manualAppointmentForm.startTime);
    const endTime = minutesToTime(startMinutes + Number(selectedManualService.duration));

    try {
      setSavingManualAppointment(true);
      await createAppointment({
        businessId: Number(user.businessId),
        serviceId: Number(selectedManualService.id),
        staffId: Number(selectedManualStaff.id),
        customerName: manualAppointmentForm.customerName.trim(),
        customerPhone: manualAppointmentForm.customerPhone.trim(),
        date: manualAppointmentForm.date,
        startTime: manualAppointmentForm.startTime,
        endTime,
        status: "confirmed",
      });

      showAlert({
        title: "Başarılı",
        message: "Randevu başarıyla oluşturuldu.",
        variant: "success",
      });
      setManualAppointmentForm((prev) => ({
        ...prev,
        serviceId: "",
        date: "",
        startTime: "",
        customerName: "",
        customerPhone: "",
      }));
      setManualTimeSlots([]);
      await loadAppointments(true);
    } catch (err) {
      console.error("Manuel randevu oluşturulamadı:", err);
      showAlert({
        title: "Hata",
        message: "Randevu oluşturulurken bir hata oluştu: " + err.message,
        variant: "error",
      });
    } finally {
      setSavingManualAppointment(false);
    }
  };

  // ---- Hizmet CRUD ----
  const openAddService = async () => {
    await loadStaff(true);
    setEditingService(null);
    setServiceForm({ name: "", price: "", duration: "", staffList: [] });
    setShowServiceModal(true);
  };

  const openEditService = async (service) => {
    await loadStaff(true);
    setEditingService(service);
    setServiceForm({
      name: service.name,
      price: String(service.price),
      duration: String(service.duration),
      staffList: Array.isArray(service.staffList) ? service.staffList : [],
    });
    setShowServiceModal(true);
  };

  const toggleServiceStaff = (staffId) => {
    setServiceForm((prev) => {
      const exists = prev.staffList.includes(staffId);
      return {
        ...prev,
        staffList: exists
          ? prev.staffList.filter((id) => id !== staffId)
          : [...prev.staffList, staffId],
      };
    });
  };

  const handleSaveService = async (e) => {
    e.preventDefault();
    const data = {
      name: serviceForm.name,
      price: Number(serviceForm.price),
      duration: Number(serviceForm.duration),
      businessId: Number(user.businessId),
      staffList: serviceForm.staffList,
    };

    if (!Array.isArray(serviceForm.staffList) || serviceForm.staffList.length === 0) {
      showAlert({
        title: "Eksik Bilgi",
        message: "Lütfen bu hizmeti verecek en az bir çalışan seçin.",
        variant: "warning",
      });
      return;
    }

    try {
      setSavingService(true);
      if (editingService) {
        await updateService(editingService.id, data);
      } else {
        await addService(data);
      }
      setShowServiceModal(false);
      await loadServices(true);
    } catch (err) {
      console.error("Hizmet kaydedilemedi:", err);
      showAlert({
        title: "Hata",
        message: "Hizmet kaydedilirken bir hata oluştu: " + err.message,
        variant: "error",
      });
    } finally {
      setSavingService(false);
    }
  };

  const handleDeleteService = async (serviceId) => {
    requestConfirmation({
      title: "Hizmeti Sil",
      message: "Bu hizmeti silmek istediğinize emin misiniz?",
      onConfirm: async () => {
        try {
          await deleteService(serviceId);
          await loadServices(true);
        } catch (err) {
          console.error("Hizmet silinemedi:", err);
          showAlert({
            title: "Hata",
            message: "Hizmet silinirken bir hata oluştu: " + err.message,
            variant: "error",
          });
        }
      },
    });
  };

  const handleCancelAppointment = async (appointmentId) => {
    requestConfirmation({
      title: "Randevuyu İptal Et",
      message: "Randevuyu iptal etmek istediğinize emin misiniz?",
      onConfirm: async () => {
        try {
          setProcessingAppointmentId(appointmentId);
          await updateAppointmentStatus(appointmentId, "cancelled");
          await loadAppointments(true);
        } catch (err) {
          console.error("Randevu iptal edilemedi:", err);
          showAlert({
            title: "Hata",
            message: "Randevu iptal edilirken bir hata oluştu: " + err.message,
            variant: "error",
          });
        } finally {
          setProcessingAppointmentId(null);
        }
      },
    });
  };

  // ---- İşletme Çalışma Saatleri ----
  const handleSaveHours = async () => {
    try {
      setSavingHours(true);
      await updateBusinessHours(user.businessId, workingHours);
      setHoursUpdated(true);
      setTimeout(() => setHoursUpdated(false), 2000);
      await loadBusiness(true);
    } catch (err) {
      console.error("Mesai saatleri güncellenemedi:", err);
      showAlert({
        title: "Hata",
        message: "Mesai saatleri kaydedilirken bir hata oluştu: " + err.message,
        variant: "error",
      });
    } finally {
      setSavingHours(false);
    }
  };

  const handleSelectLogoFile = (file) => {
    if (!file) return;

    const MAX_LOGO_FILE_SIZE = 2 * 1024 * 1024;

    const fileName = file.name.toLowerCase();
    const isPngMime = file.type === "image/png";
    const isPngExt = fileName.endsWith(".png");

    if (!isPngMime && !isPngExt) {
      showAlert({
        title: "Geçersiz Dosya",
        message: "Lütfen sadece .png formatında bir logo seçin.",
        variant: "warning",
      });
      return;
    }

    if (file.size > MAX_LOGO_FILE_SIZE) {
      showAlert({
        title: "Dosya Çok Büyük",
        message: "Logo dosyası en fazla 2MB olabilir.",
        variant: "warning",
      });
      return;
    }

    if (logoPreviewUrl) {
      URL.revokeObjectURL(logoPreviewUrl);
    }

    setSelectedLogoFile(file);
    setLogoPreviewUrl(URL.createObjectURL(file));
    setLogoUpdated(false);
  };

  const handleSaveLogo = async () => {
    if (!selectedLogoFile) {
      showAlert({
        title: "Dosya Seçilmedi",
        message: "Lütfen önce bir .png logo dosyası seçin.",
        variant: "warning",
      });
      return;
    }

    try {
      setSavingLogo(true);
      await uploadBusinessLogo(user.businessId, selectedLogoFile);
      if (logoPreviewUrl) {
        URL.revokeObjectURL(logoPreviewUrl);
      }
      setSelectedLogoFile(null);
      setLogoPreviewUrl("");
      setCurrentBusinessLogoUrl(getBusinessLogoUrl(user.businessId, Date.now()));
      setLogoUpdated(true);
      setTimeout(() => setLogoUpdated(false), 2000);
    } catch (err) {
      console.error("Logo yüklenemedi:", err);
      showAlert({
        title: "Hata",
        message: "Logo yüklenirken bir hata oluştu: " + err.message,
        variant: "error",
      });
    } finally {
      setSavingLogo(false);
    }
  };

  // ---- Kişisel Çalışma Saatleri ----
  const toggleMyClosedDay = (dayIndex) => {
    setMyClosedDays((prev) =>
      prev.includes(dayIndex)
        ? prev.filter((d) => d !== dayIndex)
        : [...prev, dayIndex]
    );
  };

  const handleSaveMyHours = async () => {
    try {
      setSavingMyHours(true);
      await updateStaffHours(user.id, {
        start: myHours.start,
        end: myHours.end,
        closedDays: myClosedDays.join(","),
        maxBookingDays: myMaxBookingDays,
      });
      setMyHoursUpdated(true);
      setTimeout(() => setMyHoursUpdated(false), 2000);
      await loadMyHours(true);
    } catch (err) {
      console.error("Çalışma saatleri güncellenemedi:", err);
      showAlert({
        title: "Hata",
        message: "Çalışma saatleri kaydedilirken bir hata oluştu: " + err.message,
        variant: "error",
      });
    } finally {
      setSavingMyHours(false);
    }
  };

  // ---- Çalışan CRUD ----
  const openAddStaff = () => {
    setStaffForm({ username: "", password: "", fullName: "", role: "staff" });
    setShowStaffModal(true);
  };

  const handleSaveStaff = async (e) => {
    e.preventDefault();
    try {
      setSavingStaff(true);
      await addStaff({
        businessId: user.businessId,
        username: staffForm.username,
        password: staffForm.password,
        fullName: staffForm.fullName,
        role: staffForm.role,
      });
      setShowStaffModal(false);
      await loadStaff(true);
    } catch (err) {
      console.error("Çalışan eklenemedi:", err);
      showAlert({
        title: "Hata",
        message: "Çalışan eklenirken bir hata oluştu: " + err.message,
        variant: "error",
      });
    } finally {
      setSavingStaff(false);
    }
  };

  const handleDeleteStaff = async (staffId) => {
    requestConfirmation({
      title: "Çalışanı Sil",
      message: "Bu çalışanı silmek istediğinize emin misiniz?",
      onConfirm: async () => {
        try {
          await deleteStaff(staffId);
          await loadStaff(true);
        } catch (err) {
          console.error("Çalışan silinemedi:", err);
          showAlert({
            title: "Hata",
            message: "Çalışan silinirken bir hata oluştu: " + err.message,
            variant: "error",
          });
        }
      },
    });
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Tab yapısı — role göre filtreleme
  const allTabs = [
    { id: "services", label: "Hizmetler", icon: ListOrdered, adminOnly: true },
    { id: "appointments", label: "Randevular", icon: CalendarDays, adminOnly: false },
    { id: "myhours", label: "Çalışma Saatlerim", icon: Clock, adminOnly: false },
    { id: "staff", label: "Çalışanlar", icon: Users, adminOnly: true },
    { id: "reports", label: "Raporlar", icon: BarChart3, adminOnly: true },
    { id: "blocked", label: "Engellenenler", icon: Ban, adminOnly: false },
    { id: "settings", label: "Ayarlar", icon: Settings, adminOnly: true },
  ];

  const tabs = isAdmin ? allTabs : allTabs.filter((t) => !t.adminOnly);

  const toLocalDateString = (dateObj) => {
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const day = String(dateObj.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getFilterStartDate = () => {
    const now = new Date();
    if (!showHistoryAppointments) {
      return toLocalDateString(now);
    }

    const oneWeekAgo = new Date(now);
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    return toLocalDateString(oneWeekAgo);
  };

  const isAppointmentPast = (apt) => {
    const normalizedEndTime = apt.endTime.length === 5 ? `${apt.endTime}:00` : apt.endTime;
    const appointmentEnd = new Date(`${apt.date}T${normalizedEndTime}`);
    if (Number.isNaN(appointmentEnd.getTime())) return false;
    return appointmentEnd < new Date();
  };

  const getCustomerAvatarClassName = (apt) => {
    if (apt.status === "cancelled") {
      return "customer-avatar is-cancelled";
    }
    if (isAppointmentPast(apt)) {
      return "customer-avatar is-past";
    }
    return "customer-avatar";
  };

  const filterStartDate = getFilterStartDate();
  const visibleAppointments = appointments.filter((apt) => apt.date >= filterStartDate);

  if (businessLoading) {
    return (
      <div className="center-screen">
        <div className="spinner" />
        <p style={{ marginTop: "1rem", color: "#94a3b8" }}>Veriler yükleniyor...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Admin Header */}
      <div className="admin-header">
        <div className="admin-header-inner">
          <div className="admin-header-left">
            <div>
              <div className="admin-header-label">
                <div className="admin-header-name">{user.fullName}</div>
                <span className={`role-badge ${user.role}`}>
                  {user.role === "admin" && <Shield />}
                  {user.role === "admin" ? "Yönetici" : "Personel"}
                </span>
              </div>
            </div>
          </div>
          <button onClick={handleLogout} className="btn-logout">
            <LogOut />
            Çıkış
          </button>
        </div>
      </div>

      <div className="admin-body">
        {/* Tabs */}
        <div className="admin-tabs">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`admin-tab ${activeTab === tab.id ? "active" : ""}`}
              >
                <Icon />
                {tab.label}
                {tab.id === "appointments" && visibleAppointments.length > 0 && (
                  <span className="admin-tab-badge">{visibleAppointments.length}</span>
                )}
              </button>
            );
          })}
        </div>

        {activeTab === "services" && isAdmin && (
          <ServicesTab
            loading={tabLoading.services}
            services={services}
            onAddService={openAddService}
            onEditService={openEditService}
            onDeleteService={handleDeleteService}
          />
        )}

        {activeTab === "appointments" && (
          <>
            <AppointmentsTab
              isAdmin={isAdmin}
              loading={tabLoading.appointments}
              appointments={visibleAppointments}
              showHistoryAppointments={showHistoryAppointments}
              onToggleHistory={() => setShowHistoryAppointments((prev) => !prev)}
              processingAppointmentId={processingAppointmentId}
              onCancelAppointment={handleCancelAppointment}
              getCustomerAvatarClassName={getCustomerAvatarClassName}
              showManualAppointmentForm={showManualAppointmentForm}
              onToggleManualAppointmentForm={() =>
                setShowManualAppointmentForm((prev) => !prev)
              }
              manualAppointmentForm={manualAppointmentForm}
              onManualFormChange={handleManualFormChange}
              staffList={staffList}
              availableManualServices={availableManualServices}
              selectedManualStaff={selectedManualStaff}
              selectedManualService={selectedManualService}
              manualSlotsLoading={manualSlotsLoading}
              manualTimeSlots={manualTimeSlots}
              onCreateManualAppointment={handleCreateManualAppointment}
              savingManualAppointment={savingManualAppointment}
              minManualDate={toLocalDateString(new Date())}
            />
          </>
        )}

        {activeTab === "myhours" && (
          <MyHoursTab
            loading={tabLoading.myhours}
            myHours={myHours}
            onChangeMyHours={(field, value) => setMyHours((h) => ({ ...h, [field]: value }))}
            myMaxBookingDays={myMaxBookingDays}
            onChangeMaxBookingDays={setMyMaxBookingDays}
            dayNames={DAY_NAMES}
            myClosedDays={myClosedDays}
            onToggleClosedDay={toggleMyClosedDay}
            onSave={handleSaveMyHours}
            saving={savingMyHours}
            updated={myHoursUpdated}
          />
        )}

        {activeTab === "staff" && isAdmin && (
          <StaffTab
            loading={tabLoading.staff}
            staffList={staffList}
            user={user}
            dayNames={DAY_NAMES}
            onAddStaff={openAddStaff}
            onDeleteStaff={handleDeleteStaff}
          />
        )}

        {activeTab === "reports" && isAdmin && (
          <ReportsTab />
        )}

        {activeTab === "blocked" && (
          <BlockedCustomers user={user} showAlert={showAlert} />
        )}

        {activeTab === "settings" && isAdmin && (
          <SettingsTab
            workingHours={workingHours}
            onChangeWorkingHours={(field, value) =>
              setWorkingHours((h) => ({ ...h, [field]: value }))
            }
            onSave={handleSaveHours}
            saving={savingHours}
            updated={hoursUpdated}
            currentLogoUrl={currentBusinessLogoUrl}
            logoPreviewUrl={logoPreviewUrl}
            onSelectLogoFile={handleSelectLogoFile}
            onSaveLogo={handleSaveLogo}
            savingLogo={savingLogo}
            logoUpdated={logoUpdated}
          />
        )}
      </div>

      <ConfirmModal
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        message={confirmState.message}
        onConfirm={handleConfirmModal}
        onCancel={closeConfirmModal}
        loading={confirmLoading}
      />

      <AlertModal
        isOpen={alertState.isOpen}
        title={alertState.title}
        message={alertState.message}
        variant={alertState.variant}
        onClose={closeAlertModal}
      />

      {/* Service Add/Edit Modal */}
      <Modal
        isOpen={showServiceModal}
        onClose={() => setShowServiceModal(false)}
        title={editingService ? "Hizmet Düzenle" : "Yeni Hizmet Ekle"}
      >
        <form onSubmit={handleSaveService} className="modal-form">
          <div>
            <label className="form-label">Hizmet Adı</label>
            <input
              type="text"
              value={serviceForm.name}
              onChange={(e) =>
                setServiceForm((f) => ({ ...f, name: e.target.value }))
              }
              placeholder="Örn: Saç Kesimi"
              required
              className="form-input"
            />
          </div>
          <div className="modal-form-row">
            <div>
              <label className="form-label">Fiyat (₺)</label>
              <input
                type="number"
                value={serviceForm.price}
                onChange={(e) =>
                  setServiceForm((f) => ({ ...f, price: e.target.value }))
                }
                placeholder="250"
                required
                min="0"
                className="form-input"
              />
            </div>
            <div>
              <label className="form-label">Süre (dk) *</label>
              <input
                type="number"
                value={serviceForm.duration}
                onChange={(e) =>
                  setServiceForm((f) => ({ ...f, duration: e.target.value }))
                }
                placeholder="30"
                required
                min="5"
                className="form-input"
              />
            </div>
          </div>
          <div>
            <label className="form-label">Bu Hizmeti Veren Çalışanlar</label>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              {staffList.length === 0 && (
                <p style={{ color: "var(--text-muted)", fontSize: "0.875rem" }}>
                  Önce en az bir çalışan eklemelisiniz.
                </p>
              )}
              {staffList.map((member) => {
                const checked = serviceForm.staffList.includes(member.id);
                return (
                  <label
                    key={member.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      fontSize: "0.9rem",
                      color: "var(--text-secondary)",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      className="service-staff-checkbox"
                      checked={checked}
                      onChange={() => toggleServiceStaff(member.id)}
                    />
                    <span>{member.fullName}</span>
                  </label>
                );
              })}
            </div>
          </div>
          <div className="modal-actions">
            <button type="submit" className="btn-submit" disabled={savingService}>
              <Save />
              {savingService
                ? "Kaydediliyor..."
                : editingService
                ? "Güncelle"
                : "Ekle"}
            </button>
            <button
              type="button"
              onClick={() => setShowServiceModal(false)}
              className="btn-cancel"
            >
              <X />
              İptal
            </button>
          </div>
        </form>
      </Modal>

      {/* Staff Add Modal */}
      <Modal
        isOpen={showStaffModal}
        onClose={() => setShowStaffModal(false)}
        title="Yeni Çalışan Ekle"
      >
        <form onSubmit={handleSaveStaff} className="modal-form">
          <div>
            <label className="form-label">Ad Soyad</label>
            <input
              type="text"
              value={staffForm.fullName}
              onChange={(e) =>
                setStaffForm((f) => ({ ...f, fullName: e.target.value }))
              }
              placeholder="Örn: Ahmet Yılmaz"
              required
              className="form-input"
            />
          </div>
          <div>
            <label className="form-label">Kullanıcı Adı</label>
            <input
              type="text"
              value={staffForm.username}
              onChange={(e) =>
                setStaffForm((f) => ({ ...f, username: e.target.value }))
              }
              placeholder="Örn: ahmet_yilmaz"
              required
              className="form-input"
            />
          </div>
          <div className="modal-form-row">
            <div>
              <label className="form-label">Şifre</label>
              <input
                type="password"
                value={staffForm.password}
                onChange={(e) =>
                  setStaffForm((f) => ({ ...f, password: e.target.value }))
                }
                placeholder="Min. 4 karakter"
                required
                minLength={4}
                className="form-input"
              />
            </div>
            <div>
              <label className="form-label">Rol</label>
              <select
                value={staffForm.role}
                onChange={(e) =>
                  setStaffForm((f) => ({ ...f, role: e.target.value }))
                }
                className="form-input"
              >
                <option value="staff">Personel</option>
                <option value="admin">Yönetici</option>
              </select>
            </div>
          </div>
          <div className="modal-actions">
            <button type="submit" className="btn-submit" disabled={savingStaff}>
              <UserPlus />
              {savingStaff ? "Ekleniyor..." : "Çalışan Ekle"}
            </button>
            <button
              type="button"
              onClick={() => setShowStaffModal(false)}
              className="btn-cancel"
            >
              <X />
              İptal
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
